/**
 * What to do at startup when the URL carries SSO tokens from Bubble AND the
 * browser already holds a session.
 *
 * Inside the Bubble app the dashboard is an iframe whose src Bubble builds
 * from the tokens it stored at the first sign-in. Every reload re-sends those
 * same tokens, even after the dashboard refreshed them on its own. Taking the
 * URL first then meant: stale tokens → refresh with an already-used refresh
 * token → failure → sign-out, while a perfectly good session sat in
 * localStorage. So: a stored session for the same user that is still valid
 * and not older than the URL's tokens wins; URL tokens are used only when
 * they are for someone else, or newer, or there is nothing stored.
 */

export interface StoredSession {
  userId: string;
  accessToken: string;
  refreshToken: string;
  coreToken: string;
  /** epoch ms; 0 when unknown */
  coreTokenExp: number;
}

export interface UrlTokens {
  userId: string;
  coreToken: string;
  /** epoch ms, or null when the URL carried none */
  expiresAt: number | null;
}

export type StartupChoice = 'url' | 'stored';

const STORAGE_KEYS = {
  userId: 'smartfilterpro_user_id',
  accessToken: 'smartfilterpro_access_token',
  refreshToken: 'smartfilterpro_refresh_token',
  coreToken: 'smartfilterpro_core_token',
  coreTokenExp: 'smartfilterpro_core_token_exp',
} as const;

/** The session in localStorage, or null when any required part is missing. */
export function readStoredSession(storage: Storage = localStorage): StoredSession | null {
  try {
    const userId = storage.getItem(STORAGE_KEYS.userId);
    const accessToken = storage.getItem(STORAGE_KEYS.accessToken);
    const coreToken = storage.getItem(STORAGE_KEYS.coreToken);
    if (!userId || !accessToken || !coreToken) return null;
    return {
      userId,
      accessToken,
      coreToken,
      refreshToken: storage.getItem(STORAGE_KEYS.refreshToken) || '',
      coreTokenExp: parseInt(storage.getItem(STORAGE_KEYS.coreTokenExp) || '0', 10) || 0,
    };
  } catch {
    return null; // storage blocked (private mode, partitioned iframe)
  }
}

/** Put a session back (used to undo a failed URL sign-in that overwrote it). */
export function writeStoredSession(session: StoredSession, storage: Storage = localStorage): void {
  try {
    storage.setItem(STORAGE_KEYS.userId, session.userId);
    storage.setItem(STORAGE_KEYS.accessToken, session.accessToken);
    storage.setItem(STORAGE_KEYS.refreshToken, session.refreshToken);
    storage.setItem(STORAGE_KEYS.coreToken, session.coreToken);
    storage.setItem(STORAGE_KEYS.coreTokenExp, String(session.coreTokenExp));
  } catch {
    // nothing to do: the session simply isn't remembered
  }
}

/**
 * 'stored' when the browser's session is for the same user, still has time
 * left, and is at least as fresh as the URL's tokens; otherwise 'url'.
 */
export function chooseStartupSession(url: UrlTokens, stored: StoredSession | null, now: number = Date.now()): StartupChoice {
  if (!stored || stored.userId !== url.userId) return 'url';
  if (!stored.coreTokenExp || stored.coreTokenExp <= now) return 'url';
  const urlExp = url.expiresAt ?? 0;
  if (urlExp > stored.coreTokenExp) return 'url'; // Bubble sent newer tokens
  return 'stored';
}

/**
 * Did Bubble (or Core) actually reject the session, as opposed to the request
 * not getting through? Only a rejection should sign the user out; a phone on a
 * flaky connection should keep its session and try again.
 */
export function isSessionRejected(err: unknown): boolean {
  const e = err as { response?: { status?: number; data?: any }; message?: string } | null;
  if (!e) return false;
  const status = e.response?.status;
  if (status === 401 || status === 403 || status === 404) return true;
  const nested = e.response?.data?.response?.status ?? e.response?.data?.status;
  if (nested === 401 || nested === 403 || nested === 404) return true;
  const msg = String(e.message || '');
  return /expired|invalid|not found|access denied|unauthorized/i.test(msg)
    && !/network error|timeout|timed out/i.test(msg);
}
